// Service layer — /v6/aircraft_configuration (H114) + the model-scoped
// /v6/aircraft_charters. Mirrors src/fuel_tanks/fuel_tanks.ts.
//
// Reads the `data` envelope key: app/pagination.py aliases Page.items -> "data".
// Reading `.items` is the recurring v6 zero-rows trap. The spread on
// `resp.data.data` is load-bearing: a wrong key THROWS (visible error) instead of
// handing DataGridPro `rows: undefined` (a white screen).
import { AxiosResponse } from "axios";
import { CacheRequestConfig } from "axios-cache-interceptor";
import queryString from "query-string";

import { axios } from "../api";
import type { AeronetV6Response } from "../types";

import type {
  AircraftConfigurationDetail,
  AircraftConfigurationRow,
  BasedOnOption,
  BasedOnRow,
  CharterRow,
  CogKind,
  CogPoint,
  ComponentLocation,
  ComponentSlotRow,
  ConfigurationOptions,
  FieldMappingRow,
  HistoryRow,
  TemplateKind,
  TemplateLimitationFields,
  TemplateRow,
} from "./types";

export const BASE = "/v6/aircraft_configuration";

export async function getAircraftConfigurations(
  params: any,
  axiosConfig?: CacheRequestConfig
): Promise<{ total: number; rows: AircraftConfigurationRow[] }> {
  const resp: AxiosResponse<AeronetV6Response<AircraftConfigurationRow>> =
    await axios.get(BASE, {
      params,
      paramsSerializer: (p) => queryString.stringify(p),
      ...axiosConfig,
      id: "get-aircraft-configuration-list",
    });
  axios.storage.remove("get-aircraft-configuration-list");
  return { total: resp.data.total, rows: [...resp.data.data] };
}

// Every read below is uncached (`cache: false`): each tab re-reads after its own
// writes, and a cached GET would show the pre-write rows.
const fresh = { cache: false as const };

export async function getConfiguration(id: number | string) {
  return (await axios.get<AircraftConfigurationDetail>(`${BASE}/${id}`, fresh)).data;
}

export async function getConfigurationOptions() {
  return (await axios.get<ConfigurationOptions>(`${BASE}/options`, { id: "aircraft-configuration-options" })).data;
}

export async function createConfiguration(body: Partial<AircraftConfigurationDetail>) {
  return (await axios.post<AircraftConfigurationDetail>(BASE, body)).data;
}

export async function updateConfiguration(id: number, body: Partial<AircraftConfigurationDetail>) {
  return (await axios.put<AircraftConfigurationDetail>(`${BASE}/${id}`, body)).data;
}

export async function deleteConfiguration(id: number) {
  await axios.delete(`${BASE}/${id}`);
}

export async function duplicateConfiguration(id: number, model: string) {
  return (await axios.post<AircraftConfigurationDetail>(`${BASE}/${id}/duplicate`, { model })).data;
}

export async function getHistory(id: number) {
  return (await axios.get<HistoryRow[]>(`${BASE}/${id}/history`, fresh)).data;
}

export async function getTemplateRows(id: number, kind: TemplateKind, config?: CacheRequestConfig) {
  return (await axios.get<TemplateRow[]>(`${BASE}/${id}/templates/${kind}`, { ...config, ...fresh })).data;
}

export async function linkTemplate(id: number, kind: TemplateKind, body: TemplateLimitationFields & { template_id: number }) {
  return (await axios.post<TemplateRow>(`${BASE}/${id}/templates/${kind}`, body)).data;
}

export async function updateTemplateRow(id: number, kind: TemplateKind, logId: number, body: TemplateLimitationFields) {
  return (await axios.put<TemplateRow>(`${BASE}/${id}/templates/${kind}/${logId}`, body)).data;
}

export async function removeTemplateRow(id: number, kind: TemplateKind, logId: number) {
  await axios.delete(`${BASE}/${id}/templates/${kind}/${logId}`);
}

export async function getBasedOnOptions(id: number) {
  return (await axios.get<BasedOnOption[]>(`${BASE}/${id}/based-on-options`, fresh)).data;
}

export async function getComponentSlots(id: number, location: ComponentLocation, config?: CacheRequestConfig) {
  return (await axios.get<ComponentSlotRow[]>(`${BASE}/${id}/components/${location}`, { ...config, ...fresh })).data;
}

export async function getCogPoints(id: number, kind: CogKind, config?: CacheRequestConfig) {
  return (await axios.get<CogPoint[]>(`${BASE}/${id}/cog/${kind}`, { ...config, ...fresh })).data;
}

export async function createCogPoint(id: number, kind: CogKind, body: Partial<CogPoint>) {
  return (await axios.post<CogPoint>(`${BASE}/${id}/cog/${kind}`, body)).data;
}

export async function updateCogPoint(id: number, kind: CogKind, pointId: number, body: Partial<CogPoint>) {
  return (await axios.put<CogPoint>(`${BASE}/${id}/cog/${kind}/${pointId}`, body)).data;
}

export async function deleteCogPoint(id: number, kind: CogKind, pointId: number) {
  await axios.delete(`${BASE}/${id}/cog/${kind}/${pointId}`);
}

export async function getBasedOnRows(id: number, config?: CacheRequestConfig) {
  return (await axios.get<BasedOnRow[]>(`${BASE}/${id}/based-on`, { ...config, ...fresh })).data;
}

export async function patchBasedOn(id: number, rowId: number, body: { active?: boolean; based_on_caption?: string }) {
  return (await axios.patch<BasedOnRow>(`${BASE}/${id}/based-on/${rowId}`, body)).data;
}

export async function getFieldMapping(id: number, config?: CacheRequestConfig) {
  return (await axios.get<FieldMappingRow[]>(`${BASE}/${id}/field-mapping`, { ...config, ...fresh })).data;
}

export async function setMappingRowChecked(id: number, rowId: number, checked: boolean) {
  await axios.patch(`${BASE}/${id}/field-mapping/rows/${rowId}`, { checked });
}

export async function setMappingFieldChecked(id: number, fieldId: number, checked: boolean) {
  await axios.patch(`${BASE}/${id}/field-mapping/fields/${fieldId}`, { checked });
}

// ── Charters: the existing /v6/aircraft_charters, scoped to the model ───────
// Legacy mod_aircraft/includes/tabs/aircraft_tab_charters.inc:38 reads
// `WHERE aircraft_type_id = X ORDER BY priority`.

export async function getModelCharters(id: number, params: { page: number; size: number }, config?: CacheRequestConfig) {
  const filter = JSON.stringify({ field: "aircraft_type_id", op: "==", value: id });
  const resp: AxiosResponse<AeronetV6Response<CharterRow>> = await axios.get("/v6/aircraft_charters", {
    params: { ...params, filter, sort: "priority" },
    paramsSerializer: (p) => queryString.stringify(p),
    ...config,
    ...fresh,
  });
  return { total: resp.data.total, rows: [...resp.data.data] };
}

export async function createCharter(body: Partial<CharterRow>) {
  return (await axios.post<CharterRow>("/v6/aircraft_charters", body)).data;
}

export async function updateCharter(charterId: number, body: Partial<CharterRow>) {
  return (await axios.put<CharterRow>(`/v6/aircraft_charters/${charterId}`, body)).data;
}

export async function deleteCharter(charterId: number) {
  await axios.delete(`/v6/aircraft_charters/${charterId}`);
}
